import { BatchUploadMetadata, FlowType, Professional } from '@domain/types';
import { DesktopSettings } from '@honestica/electron-bridge-typing';
import { bufferToFile, convertFileToDocumentJob } from '@launcher/utils';
import { onMultiFileBatchUploadStarted } from '@services/analytics/batchUploadAnalysisTracker';
import {
  beginUploadBatch,
  getBatchEndMode,
  getStartupBatchAnalyticsSource,
} from '@services/analytics/uploadAnalytics';
import electronService from '@services/electron/electron.service';
import { legacyFeatureFlagsStore } from '@services/featureFlags/legacyFeatureFlags.store';
import { logger } from '@services/logger/logger.service';
import { selectDesktopSettings } from '@store/desktop/desktop.selector';
import { selectDraftCurrentIdentity } from '@store/documents/outgoing/draft.selector';
import { notificationAddPending } from '@store/notificationWindow/notificationWindow.action';
import { UploadDocumentsActions } from '@store/uploadDocuments/uploadDocuments.slice';
import {
  getIsLifenSendingActivated,
  selectSelectedOrDefaultIdentity,
} from '@store/user/user.selector';
import { call, put, select } from 'redux-saga/effects';

function* beginUploadBatchForFlow(
  fileCount: number,
  uploadSource: Parameters<typeof beginUploadBatch>[1],
  flowType: FlowType,
) {
  const batch: BatchUploadMetadata = yield call(beginUploadBatch, fileCount, uploadSource);
  const featureFlags = legacyFeatureFlagsStore.get();

  if (fileCount > 1 && getBatchEndMode(flowType, featureFlags) === 'awaitAnalysis') {
    onMultiFileBatchUploadStarted();
  }

  return batch;
}

interface FileStats {
  isFile: boolean;
  isDirectory: boolean;
  size: number;
  mtime: Date;
}

export function createUploadAllDocsSaga(flowType: FlowType) {
  return function* uploadAllDocsSaga() {
    try {
      const isAppActivated: boolean = yield select(getIsLifenSendingActivated);

      if (!isAppActivated) {
        return;
      }

      logger.info('DESKTOP', `Checking existing files in ${flowType} folder.`);

      const settings: DesktopSettings = yield select(selectDesktopSettings);

      // Use consistent yield call syntax
      const filePaths: string[] = yield call(
        [electronService, 'getAllFilePathsFromDir'],
        settings.watchPath,
      );

      // Handle error condition early
      if (!Array.isArray(filePaths)) {
        logger.error('DESKTOP', 'Invalid file paths received');
        return;
      }

      // Process file filtering synchronously to avoid async in filter
      const cleanFilePaths: string[] = [];
      for (const path of filePaths) {
        try {
          const isFile: FileStats = yield call([electronService, 'statFile'], path);
          const baseName: string = yield call([electronService, 'getBaseName'], path);

          if (isFile.isFile && baseName !== '.DS_Store') {
            cleanFilePaths.push(path);
          }
        } catch (error) {
          logger.error('DESKTOP', `Failed to process file path`, error);
          continue;
        }
      }

      if (cleanFilePaths.length < 1) {
        return;
      }

      logger.info(
        'DESKTOP',
        `Processing ${cleanFilePaths.length} existing files in ${flowType} folder.`,
      );

      const batch: BatchUploadMetadata = yield call(
        beginUploadBatchForFlow,
        cleanFilePaths.length,
        getStartupBatchAnalyticsSource(flowType),
        flowType,
      );

      for (const path of cleanFilePaths) {
        try {
          const fileName: string = yield call([electronService, 'getBaseName'], path);

          logger.info('DESKTOP', `New file at ${flowType} watchPath`);

          const buffer: Buffer = yield call([electronService, 'readFileInWatchPath'], fileName);
          const file = bufferToFile(buffer, fileName);

          const sender: Professional = yield select((state) =>
            selectSelectedOrDefaultIdentity(state, { flowType }),
          );

          yield put(notificationAddPending({ count: 1, flowType }));

          yield put(
            UploadDocumentsActions.addUploadJob(
              convertFileToDocumentJob({
                sender,
                fileName: file.name,
                filePath: path,
                file,
                // Per-file source stays watcher: file moves + notification UI depend on it.
                uploadSource: 'watcher',
                flowType,
                ...batch,
              }),
            ),
          );
        } catch (err) {
          logger.error(
            'DESKTOP',
            `Failed to process existing file in ${flowType} folder.`,
            err,
            false,
          );
        }
      }
    } catch (error) {
      logger.error('DESKTOP', `Failed to upload all docs for ${flowType}`, error);
    }
  };
}

export function createUploadDocumentFromWatcherSaga(flowType: FlowType) {
  return function* uploadDocumentFromWatcherSaga(
    data: ReturnType<typeof UploadDocumentsActions.uploadFromWatcher>,
  ) {
    try {
      const filePaths = data.payload?.filePaths;

      if (!filePaths?.length) {
        logger.error('DESKTOP', 'No file paths provided in watcher upload');
        return;
      }

      const sender: Professional = yield select((state) =>
        selectSelectedOrDefaultIdentity(state, { flowType }),
      );

      const batch: BatchUploadMetadata = yield call(
        beginUploadBatchForFlow,
        filePaths.length,
        'watcher',
        flowType,
      );

      for (const filePath of filePaths) {
        const fileName: string = yield call([electronService, 'getBaseName'], filePath);
        const buffer: Buffer = yield call([electronService, 'readFileInWatchPath'], fileName);
        const file = bufferToFile(buffer, fileName);

        yield put(notificationAddPending({ count: 1, flowType }));

        yield put(
          UploadDocumentsActions.addUploadJob(
            convertFileToDocumentJob({
              sender,
              fileName: file.name,
              filePath,
              file,
              uploadSource: 'watcher',
              flowType,
              ...batch,
            }),
          ),
        );
      }
    } catch (err) {
      logger.error('LOGIC', 'Failed to upload document from watcher', err, false);
    }
  };
}

export function createUploadDocumentFromButtonSaga(flowType: FlowType) {
  return function* uploadDocumentFromButtonSaga(
    data: ReturnType<typeof UploadDocumentsActions.uploadFromButton>,
  ) {
    const files = data.payload?.files;

    logger.info('DESKTOP', 'uploadDocumentFromButtonSaga called', {
      flowType,
      fileCount: files?.length ?? 0,
    });

    try {
      if (!files?.length) {
        logger.error('DESKTOP', 'No files provided in button upload');
        return;
      }

      let sender: Professional;

      if (data.payload.sender) {
        sender = data.payload.sender;
      } else {
        const currentIdentity: Professional = yield select((state) =>
          selectDraftCurrentIdentity(state),
        );

        if (flowType === 'sending' && currentIdentity) {
          sender = currentIdentity;
        } else {
          sender = yield select((state) => selectSelectedOrDefaultIdentity(state, { flowType }));
        }
      }

      yield put(notificationAddPending({ count: files.length, flowType }));

      const batch: BatchUploadMetadata = yield call(
        beginUploadBatchForFlow,
        files.length,
        'button',
        flowType,
      );

      for (const file of files) {
        yield put(
          UploadDocumentsActions.addUploadJob(
            convertFileToDocumentJob({
              sender,
              fileName: file.name,
              file,
              uploadSource: 'button',
              flowType,
              ...batch,
            }),
          ),
        );
      }
    } catch (error) {
      logger.error('DESKTOP', 'Failed to upload document from button', error);
    }
  };
}

export function createUploadDocumentFromDnDSaga(flowType: FlowType) {
  return function* uploadDocumentsFromDnDSaga(
    data: ReturnType<typeof UploadDocumentsActions.uploadFromDnD>,
  ) {
    try {
      const files = data.payload?.files;

      if (!files || !Array.isArray(files)) {
        logger.error('DESKTOP', 'Invalid files provided in drag and drop upload');
        return;
      }

      if (files.length === 0) {
        return;
      }

      yield put(notificationAddPending({ count: files.length, flowType }));

      let sender: Professional;

      if (data.payload.sender) {
        sender = data.payload.sender;
      } else {
        const currentIdentity: Professional = yield select((state) =>
          selectDraftCurrentIdentity(state),
        );

        if (flowType === 'sending' && currentIdentity) {
          sender = currentIdentity;
        } else {
          sender = yield select((state) => selectSelectedOrDefaultIdentity(state, { flowType }));
        }
      }

      const batch: BatchUploadMetadata = yield call(
        beginUploadBatchForFlow,
        files.length,
        'drop',
        flowType,
      );

      const jobs = files.map((file) =>
        convertFileToDocumentJob({
          sender,
          fileName: file.name,
          file,
          uploadSource: 'drop',
          flowType,
          ...batch,
        }),
      );

      for (const job of jobs) {
        yield put(UploadDocumentsActions.addUploadJob(job));
      }
    } catch (error) {
      logger.error('DESKTOP', 'Failed to upload documents from drag and drop', error);
    }
  };
}
